"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toComparableImportHeader = exports.applyImportTransformRules = exports.ImportTransformMatchType = exports.ImportTransformRuleType = void 0;
var ImportTransformRuleType;
(function (ImportTransformRuleType) {
    ImportTransformRuleType["RenameColumn"] = "renameColumn";
    ImportTransformRuleType["DeleteColumn"] = "deleteColumn";
    ImportTransformRuleType["SplitColumn"] = "splitColumn";
    ImportTransformRuleType["ReplaceValue"] = "replaceValue";
    ImportTransformRuleType["SetDefaultValue"] = "setDefaultValue";
    ImportTransformRuleType["TrimColumn"] = "trimColumn";
    ImportTransformRuleType["UnpivotColumns"] = "unpivotColumns";
    ImportTransformRuleType["AddColumn"] = "addColumn";
    ImportTransformRuleType["PromoteRowToHeader"] = "promoteRowToHeader";
})(ImportTransformRuleType || (exports.ImportTransformRuleType = ImportTransformRuleType = {}));
var ImportTransformMatchType;
(function (ImportTransformMatchType) {
    ImportTransformMatchType["Exact"] = "exact";
    ImportTransformMatchType["Contains"] = "contains";
    ImportTransformMatchType["Regex"] = "regex";
})(ImportTransformMatchType || (exports.ImportTransformMatchType = ImportTransformMatchType = {}));
const getRuleSourceColumns = (rule) => {
    if (rule.type === ImportTransformRuleType.UnpivotColumns) {
        return rule.sources.map((source) => source.column);
    }
    if (rule.type === ImportTransformRuleType.PromoteRowToHeader) {
        return [];
    }
    return [rule.column];
};
const applyImportTransformRules = (headers, rows, rules) => {
    let currentHeaders = [...headers];
    let currentRows = rows.map((row) => ({ ...row }));
    for (const rule of rules) {
        if (rule.type !== ImportTransformRuleType.AddColumn &&
            rule.type !== ImportTransformRuleType.PromoteRowToHeader &&
            !getRuleSourceColumns(rule).some((column) => currentHeaders.includes(column))) {
            continue;
        }
        switch (rule.type) {
            case ImportTransformRuleType.RenameColumn:
                currentHeaders = currentHeaders.map((header) => (header === rule.column ? rule.newName : header));
                currentRows = currentRows.map((row) => renameRowKey(row, rule.column, rule.newName));
                break;
            case ImportTransformRuleType.DeleteColumn:
                currentHeaders = currentHeaders.filter((header) => header !== rule.column);
                currentRows = currentRows.map((row) => omitRowKey(row, rule.column));
                break;
            case ImportTransformRuleType.SplitColumn:
                if (rule.intoColumns.length === 0) {
                    break;
                }
                currentHeaders = currentHeaders.flatMap((header) => {
                    if (header === rule.column) {
                        return rule.intoColumns;
                    }
                    return rule.intoColumns.includes(header) ? [] : [header];
                });
                currentRows = currentRows.map((row) => splitRowValue(row, rule));
                break;
            case ImportTransformRuleType.ReplaceValue:
                currentRows = currentRows.map((row) => {
                    const value = row[rule.column];
                    return value === undefined ? row : { ...row, [rule.column]: replaceValueInCell(value, rule) };
                });
                break;
            case ImportTransformRuleType.SetDefaultValue:
                currentRows = currentRows.map((row) => (row[rule.column] ? row : { ...row, [rule.column]: rule.value }));
                break;
            case ImportTransformRuleType.TrimColumn:
                currentRows = currentRows.map((row) => {
                    const value = row[rule.column];
                    return value === undefined ? row : { ...row, [rule.column]: value.trim() };
                });
                break;
            case ImportTransformRuleType.UnpivotColumns:
                currentHeaders = unpivotHeaders(currentHeaders, rule);
                currentRows = currentRows.flatMap((row) => unpivotRow(row, rule));
                break;
            case ImportTransformRuleType.AddColumn:
                if (currentHeaders.some((header) => (0, exports.toComparableImportHeader)(header) === (0, exports.toComparableImportHeader)(rule.column))) {
                    break;
                }
                currentHeaders = [...currentHeaders, rule.column];
                currentRows = currentRows.map((row) => ({ ...row, [rule.column]: rule.value }));
                break;
            case ImportTransformRuleType.PromoteRowToHeader: {
                const headerRow = currentRows[rule.rowIndex - 1];
                if (!headerRow) {
                    break;
                }
                const seenNewHeaders = new Set();
                const oldToNewHeader = new Map();
                currentHeaders = currentHeaders.map((oldHeader) => {
                    const rawValue = headerRow[oldHeader]?.trim();
                    const baseName = rawValue || oldHeader;
                    let newHeader = baseName;
                    for (let suffix = 2; seenNewHeaders.has(newHeader); suffix += 1) {
                        newHeader = `${baseName} (${suffix})`;
                    }
                    seenNewHeaders.add(newHeader);
                    oldToNewHeader.set(oldHeader, newHeader);
                    return newHeader;
                });
                currentRows = currentRows.slice(rule.rowIndex).map((row) => renameRowKeys(row, oldToNewHeader));
                break;
            }
        }
    }
    return { headers: currentHeaders, rows: currentRows };
};
exports.applyImportTransformRules = applyImportTransformRules;
const renameRowKey = (row, oldKey, newKey) => {
    if (!(oldKey in row)) {
        return row;
    }
    const { [oldKey]: value, ...rest } = row;
    return { ...rest, [newKey]: value };
};
const omitRowKey = (row, key) => {
    const { [key]: _removed, ...rest } = row;
    return rest;
};
const renameRowKeys = (row, renameMap) => Object.fromEntries(Object.entries(row).map(([key, value]) => [renameMap.get(key) ?? key, value]));
const omitRowKeys = (row, keys) => keys.reduce((current, key) => omitRowKey(current, key), row);
const splitRowValue = (row, rule) => {
    const value = row[rule.column] ?? "";
    const parts = value.split(rule.delimiter).map((part) => part.trim());
    const rest = omitRowKey(row, rule.column);
    const lastIndex = rule.intoColumns.length - 1;
    rule.intoColumns.forEach((columnName, index) => {
        rest[columnName] = index === lastIndex ? parts.slice(index).join(rule.delimiter) : (parts[index] ?? "");
    });
    return rest;
};
const unpivotHeaders = (headers, rule) => {
    const sourceColumns = new Set(rule.sources.map((source) => source.column));
    const mergedHeaders = mergeSourceColumnsIntoTarget(headers, sourceColumns, rule.targetColumn);
    const newSetColumns = new Set(rule.sources
        .flatMap((source) => source.setColumns.map((entry) => entry.column))
        .filter((column) => !mergedHeaders.includes(column)));
    return [...mergedHeaders, ...newSetColumns];
};
const mergeSourceColumnsIntoTarget = (headers, sourceColumns, targetColumn) => {
    if (headers.includes(targetColumn) && !sourceColumns.has(targetColumn)) {
        return headers.filter((header) => !sourceColumns.has(header));
    }
    let targetInserted = false;
    return headers.flatMap((header) => {
        if (!sourceColumns.has(header)) {
            return [header];
        }
        if (targetInserted) {
            return [];
        }
        targetInserted = true;
        return [targetColumn];
    });
};
const unpivotRow = (row, rule) => {
    const rest = omitRowKeys(row, rule.sources.map((source) => source.column));
    return rule.sources.flatMap((source) => {
        const value = row[source.column];
        if (!value) {
            return [];
        }
        const setColumns = Object.fromEntries(source.setColumns.map((entry) => [entry.column, entry.value]));
        return [{ ...rest, [rule.targetColumn]: value, ...setColumns }];
    });
};
const toComparableImportHeader = (header) => header.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
exports.toComparableImportHeader = toComparableImportHeader;
const escapeRegExp = (input) => input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const replaceValueInCell = (value, rule) => {
    const { find, replace, matchType, caseSensitive } = rule;
    if (matchType === ImportTransformMatchType.Regex) {
        try {
            return value.replace(new RegExp(find, caseSensitive ? "g" : "gi"), replace);
        }
        catch {
            return value;
        }
    }
    if (matchType === ImportTransformMatchType.Exact) {
        const matches = caseSensitive ? value === find : value.toLowerCase() === find.toLowerCase();
        return matches ? replace : value;
    }
    const haystack = caseSensitive ? value : value.toLowerCase();
    const needle = caseSensitive ? find : find.toLowerCase();
    if (!needle || !haystack.includes(needle)) {
        return value;
    }
    return value.replace(new RegExp(escapeRegExp(find), caseSensitive ? "g" : "gi"), replace);
};
